import { OCLTradingSettings } from '@pages/ocl-trading/types/types';
import { Filter, FilterType } from '@shared/protos/filter';
import { ColDef } from 'ag-grid-enterprise';
import { formatDatetime } from '@shared/utils/date';
import { OtcExchange, OtcOrderType } from './v2/otcQuote';

type LicenceEnum = 'trial' | 'standard_paid' | 'unspecified';

export type ProductGroupEnum =
  | 'unspecified'
  | 'crude'
  | 'gasoline'
  | 'naphtha'
  | 'distillate'
  | 'jet_fuel'
  | 'fuel_oil'
  | 'petrochemical'
  | 'freight'
  | 'methane'
  | 'ethane'
  | 'propane'
  | 'butane'
  | 'pentane'
  | 'ngl'
  | 'refinery_margin'
  | 'agriculture'
  | 'power'
  | 'marine_fuel';

// Every group except 'unspecified', which the API rejects as a link scope
export const PRODUCT_GROUP_OPTIONS: ProductGroupEnum[] = [
  'crude',
  'gasoline',
  'naphtha',
  'distillate',
  'jet_fuel',
  'fuel_oil',
  'petrochemical',
  'freight',
  'methane',
  'ethane',
  'propane',
  'butane',
  'pentane',
  'ngl',
  'refinery_margin',
  'agriculture',
  'power',
  'marine_fuel',
];

export enum ClientEnum {
  UNSPECIFIED = 'unspecified',
  WEB = 'web',
  MOBILE = 'mobile',
  EXCEL = 'excel',
  API = 'api',
  WHATSAPP = 'whatsapp',
}

export enum PermissionsEnum {
  UNSPECIFIED = 'unspecified',
  TRADING = 'trading',
  LIVE_DATA = 'live_data',
  HISTORICAL_DATA = 'historical_data',
  FLUX_ADMIN = 'flux_admin',
  TRADING_ADMIN = 'trading_admin',
  ONYX_USER = 'onyx_user',
  LADDER = 'ladder',
  USER_ADMIN = 'user_admin',
  USER_ADMIN_RO = 'user_admin_ro',
  USER_DT = 'dt_user',
  THE_OFFICIALS_API_USER = 'the_officials_api_user',
  FREEMIUM = 'freemium_user',
  FREEMIUM_ADMIN = 'freemium_admin',
  AXIS = 'axis_user',
  BROKER = 'broker',
  FLUX_SNR_ENG_ADMIN = 'flux_snr_eng_admin',
  ONYXLENS = 'onyxlens',
  OFFICIALS_ADMIN = 'officials_admin',
  DATA_ADMIN = 'data_admin',
  OFFICIALS = 'officials',
  MARKS_INTERNAL_WRITE = 'marks_internal_write',
  MARKS_INTERNAL_READ = 'marks_internal_read',
  AGENT_ADMIN = 'agent_admin',
}

export type SnapshotPeriod = 'unspecified' | 'singapore_1630' | 'london_1200' | 'london_1630' | 'london_1800' | 'london_1930';

export type TradeLimit = { symbol: string; limit: number; exchange: OtcExchange[]; order_types: OtcOrderType[] };
export type LadderLimit = { symbol: string; limit: number };
export type User = {
  id: string;
  account_name: string;
  full_name: string;
  email: string;
  organization_id: number;
  trading_accounts?: string[];
  superuser: boolean;
  modified_at: string;
  whatsapp_number: string;
  products: string[];
  product_groups: ProductGroupEnum[];
  client_type_list: ClientEnum[];
  license_expiry: string;
  license_type: LicenceEnum;
  snapshot_periods: SnapshotPeriod[];
  access_live_data: boolean;
  access_historical_data: boolean;
  is_flux_admin: boolean;
  api_token: string;
  client_type: ClientEnum;
  allow_trading: boolean;
  trade_limits: TradeLimit[];
  abn_trad_accounts: string[];
  is_trading_admin: boolean;
  websocket_limit: number;
  cot_product_groups: string[];
  permissions: PermissionsEnum[];
  ladder_limits: LadderLimit[];
  slippage_bp: number;
  research_reports: string[];
};

export type UserSettings = {
  dashboard: Record<string, any>;
  widgets: Record<string, any>;
  ocl_trading: OCLTradingSettings;
};

export const USER_COLUMNS: ColDef<User>[] = [
  {
    field: 'id',
    headerName: 'ID',
  },
  {
    field: 'account_name',
    headerName: 'Account Name',
  },
  {
    field: 'full_name',
    headerName: 'Full Name',
  },
  {
    field: 'email',
    headerName: 'Email',
  },
  {
    field: 'client_type_list',
    headerName: 'Client Type List',
    valueFormatter: ({ value }) => value.join(', '),
  },
  {
    field: 'permissions',
    headerName: 'Permissions',
    valueFormatter: ({ value }) => value.join(', '),
  },
  {
    field: 'license_expiry',
    headerName: 'License Expiry',
    valueFormatter: ({ value }) => {
      if (!value) return '';
      return formatDatetime(value);
    },
  },
  {
    field: 'modified_at',
    headerName: 'Modified At',
    valueFormatter: ({ value }) => {
      if (!value) return '';
      return formatDatetime(value);
    },
  },
];

// Every permission the API will accept for the `permission` filter. Derived from the enum
// rather than hand-listed: as a subset it had drifted six values behind. The filter panel
// sorts the options itself, so declaration order does not matter.
const PERMISSIONS_TYPE_OPTIONS: PermissionsEnum[] = Object.values(PermissionsEnum).filter(permission => permission !== PermissionsEnum.UNSPECIFIED);

// The permissions an admin can grant from the user page. PATCH /users/{id} replaces this slice
// wholesale, so every save must send the full list back or the omitted ones are revoked.
export const ADMIN_MANAGED_PERMISSIONS: PermissionsEnum[] = [
  PermissionsEnum.TRADING,
  PermissionsEnum.LADDER,
  PermissionsEnum.FLUX_SNR_ENG_ADMIN,
  PermissionsEnum.ONYXLENS,
  PermissionsEnum.OFFICIALS_ADMIN,
  PermissionsEnum.DATA_ADMIN,
  PermissionsEnum.AGENT_ADMIN,
];

// Managed by UserPermission.managed_by_admin() in quant-data but deliberately not grantable
// from this UI. They still have to be echoed back on every save, or the backend subtracting
// its managed slice revokes them. ADMIN_MANAGED_PERMISSIONS plus these must cover it.
export const PRESERVED_PERMISSIONS: PermissionsEnum[] = [PermissionsEnum.MARKS_INTERNAL_WRITE, PermissionsEnum.MARKS_INTERNAL_READ];

// What a save must send: what the admin chose, plus anything managed we do not offer
export const permissionsPayload = (current: PermissionsEnum[], edited: PermissionsEnum[]): PermissionsEnum[] => [
  ...edited,
  ...current.filter(permission => PRESERVED_PERMISSIONS.includes(permission)),
];

export const USER_FILTERS: Filter[] = [
  { type: FilterType.AUTOCOMPLETE, name: 'permission', label: 'Permissions', options: PERMISSIONS_TYPE_OPTIONS },
];
export type ApiToken = {
  api_token: string;
};
